import { rgbAt } from '../image/analysis.js';
import { LANDMARKS, pick } from '../geometry/landmarks.js';
import type { NormalizedLandmark } from '../types/geometry.js';
import { clamp, clamp01, roundTo } from '../types/geometry.js';
import type { AnalysisBuffer } from '../types/image.js';
import type { FaceGeometryMetrics } from '../types/metrics.js';
import { MOUTH_CORNER_INDICES } from './expression.js';
import { localDetailAt, pixelFeatures } from './hairOcclusion.js';

/**
 * Mouth-open analysis for the «Рот закрыт» check.
 *
 * Deliberately independent from the expression analysis: this module measures
 * the *geometry of the lips* (inner-lip gap and mouth aspect ratio) and, when
 * the model provides them, the `jawOpen` / `mouthClose` blendshapes. It never
 * looks at the smile muscles, so an open mouth and a smile cannot be confused.
 *
 * The gap is normalized to the face height, which makes the same threshold work
 * for a close-up and for a distant portrait.
 */

export interface MouthAnalysisInput {
  landmarks?: readonly NormalizedLandmark[];
  geometry?: FaceGeometryMetrics;
  blendshapes?: Record<string, number>;
  pixels?: AnalysisBuffer;
}

export interface MouthAnalysis {
  /** Inner-lip gap normalized to the face height, 0..1. */
  lipGap: number;
  /** Raw landmark gap in image coordinates (debug only). */
  lipGapRaw: number;
  /** Inner-lip gap over mouth width; 0 when the corners are unreliable. */
  mouthAspectRatio: number;
  /** Normalized mouth width in image coordinates. */
  mouthWidth: number;
  /** False when the corner landmarks are missing/implausible. */
  mouthWidthReliable: boolean;
  jawOpen: number | undefined;
  mouthClose: number | undefined;
  /** Combined blendshape openness, 0..1; `undefined` without blendshapes. */
  blendshapeOpen: number | undefined;
  teethVisible: boolean | undefined;
  confidence: number;
  notes: string[];
}

const channel = (shapes: Record<string, number> | undefined, name: string): number | undefined =>
  typeof shapes?.[name] === 'number' ? clamp01(shapes[name] as number) : undefined;

/** Bright, achromatic pixels inside the lip gap — a teeth proxy. */
const detectTeeth = (
  pixels: AnalysisBuffer,
  mouthCenter: { x: number; y: number },
  mouthWidth: number,
  upperLip: { x: number; y: number } | null,
  lowerLip: { x: number; y: number } | null,
): boolean | undefined => {
  if (!upperLip || !lowerLip) return undefined;
  const { data, width, height, channels, luma } = pixels;
  const top = Math.min(upperLip.y, lowerLip.y);
  const bottom = Math.max(upperLip.y, lowerLip.y);
  const half = Math.max(mouthWidth, 0.02) / 2;
  const x0 = clamp(Math.floor((mouthCenter.x - half) * width), 0, width - 1);
  const x1 = clamp(Math.ceil((mouthCenter.x + half) * width), x0 + 1, width);
  const y0 = clamp(Math.floor(top * height), 0, height - 1);
  const y1 = clamp(Math.ceil(bottom * height), y0 + 1, height);

  let bright = 0;
  let total = 0;
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const colour = rgbAt(data, width, channels, x, y);
      const features = pixelFeatures(colour.r, colour.g, colour.b);
      total += 1;
      const detail = localDetailAt(luma, width, height, x, y);
      if (
        features.luma >= 150 &&
        features.saturation <= 0.15 &&
        detail < 90 &&
        features.r >= 150 &&
        features.g >= 150 &&
        features.b >= 150
      ) {
        bright += 1;
      }
    }
  }
  if (total === 0) return undefined;
  return bright / total >= 0.12;
};

export const analyseMouth = (input: MouthAnalysisInput): MouthAnalysis => {
  const geometry = input.geometry;
  const landmarks = input.landmarks ?? geometry?.landmarks ?? [];
  const notes: string[] = [];

  const upperLip = pick(landmarks, LANDMARKS.upperLipInner);
  const lowerLip = pick(landmarks, LANDMARKS.lowerLipInner);
  const mouthCenter =
    geometry?.mouthCenter ??
    (upperLip && lowerLip
      ? { x: (upperLip.x + lowerLip.x) / 2, y: (upperLip.y + lowerLip.y) / 2 }
      : null);

  const corners: Array<{ x: number; y: number }> = [];
  for (const index of MOUTH_CORNER_INDICES) {
    const point = pick(landmarks, index);
    if (point) corners.push(point);
  }
  const cornerWidth =
    corners.length >= 2
      ? Math.abs((corners[0]?.x as number) - (corners[1]?.x as number))
      : 0;

  const faceHeight =
    geometry && geometry.faceHeight > 0
      ? geometry.faceHeight
      : upperLip && lowerLip
        ? Math.max(1e-3, Math.abs(lowerLip.y - upperLip.y) * 8)
        : 0;
  const faceWidth = geometry && geometry.faceBox.width > 0 ? geometry.faceBox.width : cornerWidth;

  if (!upperLip || !lowerLip || faceHeight <= 0 || !mouthCenter) {
    return {
      lipGap: 0,
      lipGapRaw: 0,
      mouthAspectRatio: 0,
      mouthWidth: cornerWidth,
      mouthWidthReliable: false,
      jawOpen: channel(input.blendshapes, 'jawOpen'),
      mouthClose: channel(input.blendshapes, 'mouthClose'),
      blendshapeOpen: combineBlendshapes(input.blendshapes),
      teethVisible: undefined,
      confidence: 0.2,
      notes: ['Недостаточно landmarks для измерения положения рта.'],
    };
  }

  const lipGapRaw = Math.abs(lowerLip.y - upperLip.y);
  const lipGap = clamp01(lipGapRaw / faceHeight);
  const mouthWidthReliable = cornerWidth >= Math.max(1e-3, faceWidth * 0.12);
  if (!mouthWidthReliable) {
    notes.push('Уголки рта определены неуверенно: соотношение сторон рта не использовалось.');
  }
  const mouthAspectRatio = mouthWidthReliable && cornerWidth > 0 ? lipGapRaw / cornerWidth : 0;

  const jawOpen = channel(input.blendshapes, 'jawOpen');
  const mouthClose = channel(input.blendshapes, 'mouthClose');
  const blendshapeOpen = combineBlendshapes(input.blendshapes);

  let confidence = clamp01(0.5 + 0.35 * clamp01(geometry?.poseConfidence?.value ?? 0.6));
  if (!mouthWidthReliable) confidence = clamp01(confidence - 0.1);
  if (blendshapeOpen !== undefined) {
    const geometryOpen = clamp01(lipGap / 0.15);
    if (Math.abs(blendshapeOpen - geometryOpen) <= 0.5) {
      confidence = clamp01(confidence + 0.15);
    } else {
      confidence = clamp01(confidence + 0.05);
      notes.push('Модель и геометрия губ расходятся: вердикт опирается на оба сигнала.');
    }
  }

  return {
    lipGap: roundTo(lipGap, 4),
    lipGapRaw: roundTo(lipGapRaw, 4),
    mouthAspectRatio: roundTo(mouthAspectRatio, 3),
    mouthWidth: roundTo(cornerWidth, 4),
    mouthWidthReliable,
    jawOpen,
    mouthClose,
    blendshapeOpen,
    teethVisible: input.pixels
      ? detectTeeth(input.pixels, mouthCenter, cornerWidth > 0 ? cornerWidth : faceWidth * 0.4, upperLip, lowerLip)
      : undefined,
    confidence: roundTo(confidence, 3),
    notes,
  };
};

const combineBlendshapes = (
  shapes: Record<string, number> | undefined,
): number | undefined => {
  const jaw = channel(shapes, 'jawOpen');
  const close = channel(shapes, 'mouthClose');
  if (jaw === undefined && close === undefined) return undefined;
  return clamp01(Math.max(jaw ?? 0, close !== undefined ? 1 - close : 0));
};
